"use client";

import { HeaderContainer } from "@bumble/ui-kit";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import locales from "../../../../locales.json";
import { useStore } from "@/lib/state";
import { resolveDefaultLocale } from "@/lib/defaultLocale";
import { addLocalePrefix } from "@/lib/utils/addLocalePrefix";
import { setLocaleCookie } from "@/lib/localeCookie";

const languages = Object.entries(locales).map(([value, title]) => ({
  value,
  title,
}));

interface SubMenuItem {
  id: string;
  title: string;
  description?: string | null;
  iconName?: string | null;
  url: string;
}

interface MenuItem {
  id: string;
  title: string;
  url?: string | null;
  hasSubmenu: boolean;
  subMenuTitle?: string | null;
  subMenuTitleUrl?: string | null;
  subMenuDescription?: string | null;
  subMenuItems?: SubMenuItem[];
}

export interface HeaderData {
  backButtonText?: string | null;
  downloadA11yLabel?: string | null;
  downloadUrl?: string | null;
  getTheAppText?: string | null;
  logoA11yLabel?: string | null;
  signInText?: string | null;
  signInUrl?: string | null;
  menuItems: MenuItem[];
}

interface Props {
  data: HeaderData | null;
  currentLocale: string;
  availableLocales: string[];
}

export default function Header({
  data,
  currentLocale,
  availableLocales,
}: Props) {
  const router = useRouter();
  const setLanguage = useStore((state) => state.setLanguage);

  useEffect(() => {
    setLanguage(currentLocale);
  }, [currentLocale, setLanguage]);

  const handleLanguageChange = (lang: string) => {
    const targetLocale = availableLocales.includes(lang)
      ? lang
      : resolveDefaultLocale(availableLocales);
    setLanguage(targetLocale);
    setLocaleCookie(targetLocale);
    router.push(`/${targetLocale}`);
  };

  const menuItems =
    data?.menuItems.map((item) => {
      if (
        item.hasSubmenu &&
        item.subMenuItems &&
        item.subMenuItems.length > 0
      ) {
        return {
          title: item.title,
          subMenu: {
            title: item.subMenuTitle ?? "",
            titleUrl: item.subMenuTitleUrl
              ? addLocalePrefix(item.subMenuTitleUrl, currentLocale)
              : undefined,
            description: item.subMenuDescription ?? "",
            backButton: {
              text: data.backButtonText ?? "Back",
              onClick: () => {},
            },
            items: item.subMenuItems.map((subItem) => ({
              title: subItem.title,
              description: subItem.description ?? "",
              iconName: subItem.iconName ?? undefined,
              url: addLocalePrefix(subItem.url, currentLocale),
            })),
          },
        };
      }
      return {
        title: item.title,
        url: addLocalePrefix(item.url, currentLocale) ?? "",
      };
    }) ?? [];

  return (
    <HeaderContainer
      menuItems={menuItems}
      backButtonText={data?.backButtonText ?? "Back"}
      languages={languages}
      lang={currentLocale}
      downloadUrl={data?.downloadUrl ?? "https://bumble.com/download"}
      downloadA11yLabel={data?.downloadA11yLabel ?? "Download the app"}
      signInText={data?.signInText ?? "Sign in"}
      signInUrl={addLocalePrefix(data?.signInUrl ?? "/sign-in", currentLocale)}
      getTheAppText={data?.getTheAppText ?? "Get the app"}
      logoA11yLabel={data?.logoA11yLabel ?? "Bumble logo"}
      hamburgerMenuOpenA11yLabel="Open navigation menu"
      hamburgerMenuCloseA11yLabel="Close navigation menu"
      languageA11yLabel="Choose your language"
      onLanguageChange={handleLanguageChange}
      isDarkMode={false}
    />
  );
}
